import { useEffect, useState, type ReactElement } from "react";

import { api, apiBlob, ApiError } from "../../api/client";
import { longDate } from "../../lib/date";
import { ConfirmDialog } from "../ConfirmDialog";
import { PdfViewer } from "../PdfViewer";
import type {
  ConsentDocumentOut,
  MgbPatientStatus,
  ParticipantProfile,
  ResolvedProtocol,
} from "../../api/types";

// Full legacy-parity consent (docs/archive/DESIGN.md §12a). Two shapes, mirroring the
// one rule in docs/DOMAIN_MODEL.md — every participant sits under exactly one
// of the IRB studies:
// - Substudy participants (protocol.in_substudy) get their consent loaded
//   directly: their substudy's own sheet, or its parent IRB study's top-level
//   sheet when it has none. The MGB-patient question is never asked — the
//   substudy already fixes the IRB study, so the answer stays unrecorded.
// - Everyone else answers the MGB-patient question, and that answer is what
//   routes them to one of the two top-level information sheets.
// Then: PDF viewer → name/DOB capture → live acknowledgement → explicit agree →
// certification step (REDCap eConsent parity: review the COMPILED signed PDF
// inline and certify it is correct) → e-signature → PUT /consent.

const PATIENT_OPTIONS: { value: MgbPatientStatus; label: string }[] = [
  { value: 1, label: "Yes, a current or former patient" },
  { value: 0, label: "No" },
  { value: -1, label: "Prefer not to answer" },
];

export function ConsentFlow({
  profile,
  protocol,
  onChange,
}: {
  profile: ParticipantProfile | null;
  protocol: ResolvedProtocol | null;
  onChange: () => Promise<void>;
}): ReactElement {
  const consented = profile?.enrollment_status === "active";
  const inSubstudy = protocol?.in_substudy === true;
  const studyName = protocol?.study?.name ?? null;

  const [patientStatus, setPatientStatus] = useState<MgbPatientStatus | null>(null);
  const [doc, setDoc] = useState<ConsentDocumentOut | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState<boolean | null>(null);
  const [showSign, setShowSign] = useState(false);
  // Certification step (eConsent parity): the compiled signed-PDF preview the
  // participant must review and certify before the final submit.
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [certified, setCertified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  async function loadDocument(query: string): Promise<void> {
    setError(null);
    try {
      const d = await api<ConsentDocumentOut>(
        "GET",
        `/participants/me/consent/document${query ? `?${query}` : ""}`,
      );
      setDoc(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the consent document.");
    }
  }

  // Study consents need no gate answer — the server resolves the study's sheet.
  useEffect(() => {
    if (consented || !inSubstudy) return;
    void loadDocument("");
  }, [consented, inSubstudy]);

  async function chooseStatus(value: MgbPatientStatus): Promise<void> {
    setPatientStatus(value);
    await loadDocument(`patient_status=${value}`);
  }

  function consentBody(): Record<string, unknown> | null {
    if (!doc || (!inSubstudy && patientStatus === null)) return null;
    return {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dob,
      // Optional add-an-email (study-ID accounts have none); blank sends nothing.
      ...(profile?.email == null && email.trim() ? { email: email.trim() } : {}),
      // On a substudy consent the server derives its parent IRB study;
      // the question is only asked (and sent) on the top-level paths.
      ...(inSubstudy ? {} : { mgb_patient_status: patientStatus }),
      document_version: doc.document_version,
      agreed: true,
      signature_typed_name: `${firstName} ${lastName}`.trim(),
    };
  }

  // Certification step (eConsent parity): render the compiled signed PDF —
  // exactly what will be stored — for inline review before the e-signature.
  async function openReview(): Promise<void> {
    const body = consentBody();
    if (!body) return;
    // Email is optional — but if one was typed, catch an obvious typo here
    // with a clear message instead of a raw 422 from the server.
    if (profile?.email == null && email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter a valid email address, or leave the email field empty.");
      return;
    }
    setBusy(true);
    setError(null);
    setCertified(false);
    try {
      const pdf = await apiBlob("POST", "/participants/me/consent/preview", { json: body });
      setPreviewBlob(pdf);
      setShowSign(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not prepare your consent document.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(): Promise<void> {
    const body = consentBody();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api<ParticipantProfile>("PUT", "/participants/me/consent", { json: body });
      setShowSign(false);
      setPreviewBlob(null);
      await onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not record consent.");
    } finally {
      setBusy(false);
    }
  }

  if (consented) {
    return (
      <p className="muted">
        <i className="fas fa-check-circle status-success" /> Consent recorded
        {profile?.consent_version ? ` (${profile.consent_version})` : ""}
        {profile?.consented_at ? ` on ${new Date(profile.consented_at).toLocaleDateString()}` : ""}.
      </p>
    );
  }

  const canSubmit =
    (inSubstudy || patientStatus !== null) &&
    !!doc &&
    firstName.trim() &&
    lastName.trim() &&
    dob &&
    agreed === true;

  return (
    <div className="consent-flow">
      <p className="muted">
        {inSubstudy && studyName ? (
          <>
            You&apos;re joining <strong>{studyName}</strong> — please review the study&apos;s
            information sheet carefully. Consent is required once.
          </>
        ) : (
          "Please review the information carefully. Consent is required once."
        )}
      </p>

      {/* Patient-status gate (routes to one of the two top-level IRB sheets).
          Skipped when the participant's study carries its own consent. */}
      {!inSubstudy ? (
        <fieldset className="consent-block">
          <legend>
            Are you currently, or have you ever been, a patient at Mass General Brigham?
          </legend>
          <div className="choice-row">
            {PATIENT_OPTIONS.map((opt) => (
              <label key={opt.value} className={`choice${patientStatus === opt.value ? " selected" : ""}`}>
                <input
                  type="radio"
                  name="patient_status"
                  checked={patientStatus === opt.value}
                  onChange={() => void chooseStatus(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {/* The routed consent PDF. */}
      {doc ? (
        <div className="consent-block">
          <div className="consent-doc-head">
            <span>Research Information Sheet</span>
            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-sm">
              <i className="fas fa-download" aria-hidden="true" /> Download
            </a>
          </div>
          <PdfViewer file={doc.url} title="Consent Document" />
        </div>
      ) : null}

      {/* Participant info + live acknowledgement. */}
      {doc ? (
        <div className="consent-block">
          <div className="field-grid">
            <label className="field">
              <span>First name</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={30} />
            </label>
            <label className="field">
              <span>Last name</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={30} />
            </label>
            <label className="field">
              <span>Date of birth</span>
              <input type="date" max={today} value={dob} onChange={(e) => setDob(e.target.value)} />
            </label>
            <label className="field">
              <span>{profile?.email ? "Email" : "Email (optional)"}</span>
              {profile?.email ? (
                <input value={profile.email} readOnly disabled />
              ) : (
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={254}
                />
              )}
            </label>
          </div>

          <div className="consent-ack">
            I, <strong>{firstName || "…"} {lastName || ""}</strong>
            {dob ? <> (born {longDate(dob)})</> : null}, acknowledge that I have reviewed the
            information sheet dated {new Date().toLocaleString()}.
          </div>

          <div className="choice-row">
            <label className={`choice${agreed === true ? " selected" : ""}`}>
              <input type="radio" name="agree" checked={agreed === true} onChange={() => setAgreed(true)} />
              I agree to participate in this research
            </label>
            <label className={`choice${agreed === false ? " selected" : ""}`}>
              <input type="radio" name="agree" checked={agreed === false} onChange={() => setAgreed(false)} />
              I do not agree
            </label>
          </div>
        </div>
      ) : null}

      {error && !showSign ? <p className="form-error">{error}</p> : null}
      <button className="btn-primary-glow" disabled={!canSubmit || busy} onClick={() => void openReview()}>
        {busy && !showSign ? "Preparing your document…" : "Review & sign"}
      </button>

      {/* Certification + e-signature modal (eConsent parity): the participant
          reviews the compiled signed PDF — footer-stamped pages + attestation,
          exactly what will be stored — and certifies it before submitting. */}
      {showSign ? (
        <ConfirmDialog
          title="Review & Electronically Sign"
          confirmLabel="Submit"
          busyLabel="Submitting…"
          cancelLabel="Go back"
          busy={busy}
          confirmDisabled={!certified}
          wide
          error={error}
          onConfirm={() => void submit()}
          onCancel={() => {
            setShowSign(false);
            setPreviewBlob(null);
            setCertified(false);
          }}
        >
          {previewBlob ? (
            <PdfViewer file={previewBlob} title="Your signed consent document" />
          ) : null}
          <label className="choice" style={{ display: "block", marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              checked={certified}
              onChange={(e) => setCertified(e.target.checked)}
            />{" "}
            I certify that all of my information in the document above is correct.
          </label>
          <p className="muted">
            I understand that clicking “Submit” electronically signs this form, equivalent to
            signing a physical document.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
